import { LogManager } from '@src/utility/Logger';

import type { TranscriptToken } from '../types';
import type { ImageKey } from '@src/types.ts/general';
import type { PunchListItem as PunchListItemData } from '@src/types.ts/story';

const logger = LogManager.getLogger('editor:PunchListItem');

/**
 * Domain model for punch list items in the editor.
 * Holds runtime state with required stable initial indices derived from transcript.
 * Provides toJSON() for serialization to store layer.
 */
export class PunchListItem {
  public readonly id?: string;
  public readonly type?: ImageKey;
  public readonly sub_type?: 'manual' | 'ai';
  public readonly elementId: string;
  public artifactSrc?: string;
  public artifactAspectRatio?: number;
  public matchReason?: string;
  public artifactDescription?: string;
  public similarityScore?: string;
  public line: string;
  public description: string;
  public evocative: string;
  public metadata: Record<string, string>;
  public genAiPrompt?: string;
  public dallePrompt: string;
  public dalleImages: Record<'uploadId' | 'uploadUrl', string>[];
  public stockOptions?: Record<'id' | 'url' | 'description', string>[];
  public stockKeywords: string;

  // Transcript position with required stable initial indices
  public transcriptPosition: {
    startIndex: number;
    endIndex: number;
    initialStartIndex: number;
    initialEndIndex: number;
  };

  public duration?: number;
  public startTime?: number;
  public endTime?: number;

  constructor(
    data: PunchListItemData & {
      transcriptPosition: {
        startIndex: number;
        endIndex: number;
        initialStartIndex?: number;
        initialEndIndex?: number;
      };
    },
  ) {
    this.id = data.id;
    this.type = data.type;
    this.sub_type = data.sub_type;
    this.elementId = data.elementId;
    this.artifactSrc = data.artifactSrc;
    this.artifactAspectRatio = data.artifactAspectRatio;
    this.matchReason = data.matchReason;
    this.artifactDescription = data.artifactDescription;
    this.similarityScore = data.similarityScore;
    this.line = data.line;
    this.description = data.description;
    this.evocative = data.evocative;
    this.metadata = data.metadata;
    this.genAiPrompt = data.genAiPrompt;
    this.dallePrompt = data.dallePrompt;
    this.dalleImages = data.dalleImages;
    this.stockOptions = data.stockOptions;
    this.stockKeywords = data.stockKeywords;
    this.duration = data.duration;
    this.startTime = data.startTime;
    this.endTime = data.endTime;

    // Store position with required initial indices
    this.transcriptPosition = {
      startIndex: data.transcriptPosition.startIndex,
      endIndex: data.transcriptPosition.endIndex,
      initialStartIndex:
        data.transcriptPosition.initialStartIndex ??
        data.transcriptPosition.startIndex,
      initialEndIndex:
        data.transcriptPosition.initialEndIndex ??
        data.transcriptPosition.endIndex,
    };
  }

  /**
   * Creates a PunchListItem from store data with initial indices derived from transcript.
   * @param data - Punch list data from store (PunchListManager)
   * @param transcript - Full transcript to derive initial indices
   * @returns PunchListItem instance, or null if initial indices cannot be derived
   */
  static fromData(
    data: PunchListItemData,
    transcript: TranscriptToken[],
  ): PunchListItem | null {
    const { transcriptPosition, elementId } = data;

    // Derive initial indices from transcript
    const startToken = transcript[transcriptPosition.startIndex];
    const endToken = transcript[transcriptPosition.endIndex];

    if (startToken?.initial_index == null || endToken?.initial_index == null) {
      logger.warn('Cannot derive initial indices for punch list item', {
        elementId,
        startIndex: transcriptPosition.startIndex,
        endIndex: transcriptPosition.endIndex,
        hasStartToken: !!startToken,
        hasEndToken: !!endToken,
        startTokenInitialIndex: startToken?.initial_index,
        endTokenInitialIndex: endToken?.initial_index,
      });
      return null; // Skip items with invalid indices
    }

    const initialStartIndex = startToken.initial_index;
    const initialEndIndex = endToken.initial_index;

    return new PunchListItem({
      ...data,
      transcriptPosition: {
        startIndex: transcriptPosition.startIndex,
        endIndex: transcriptPosition.endIndex,
        initialStartIndex,
        initialEndIndex,
      },
    });
  }

  /**
   * Serializes this model to JSON format for store layer consumption.
   * Returns the format expected by PunchListManager.
   * Note: initialStartIndex/initialEndIndex are included for editor operations
   * but are not part of the persisted PunchListItem type.
   */
  toJSON(): PunchListItemData & {
    transcriptPosition: {
      startIndex: number;
      endIndex: number;
    };
  } {
    return {
      id: this.id,
      type: this.type,
      sub_type: this.sub_type,
      elementId: this.elementId,
      artifactSrc: this.artifactSrc,
      artifactAspectRatio: this.artifactAspectRatio,
      matchReason: this.matchReason,
      artifactDescription: this.artifactDescription,
      similarityScore: this.similarityScore,
      line: this.line,
      description: this.description,
      evocative: this.evocative,
      metadata: this.metadata,
      genAiPrompt: this.genAiPrompt,
      dallePrompt: this.dallePrompt,
      dalleImages: this.dalleImages,
      stockOptions: this.stockOptions,
      stockKeywords: this.stockKeywords,
      transcriptPosition: {
        startIndex: this.transcriptPosition.startIndex,
        endIndex: this.transcriptPosition.endIndex,
      },
      duration: this.duration,
      startTime: this.startTime,
      endTime: this.endTime,
    };
  }
}
